import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Input } from '@repo/ui/input';
import { ScrollArea } from '@repo/ui/scroll-area';
import { useStep } from '@repo/ui/stepper';
import { CheckCircle2Icon, SearchIcon, UserIcon, UserPlusIcon } from 'lucide-react';
import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { trpc } from '@/providers/trpc';
import type { RouterOutputs } from '@/providers/trpc-client';

export const ZNewOwnerSchema = z.object({
  firstName: z.string().min(1, t('auth.firstNameIsRequired')),
  lastName: z.string().min(1, t('auth.lastNameIsRequired')),
  phone: z.string().optional(),
  email: z.email(t('extra.invalidEmailAddress')).min(1, t('extra.emailIsRequired')),
});

export type TNewOwner = z.infer<typeof ZNewOwnerSchema>;

export type TOwnerData =
  | { mode: 'new'; data: TNewOwner }
  | { mode: 'existing'; userId: string; email: string };

type Props = {
  form: UseFormReturn<TNewOwner>;
  onNext: (data: TOwnerData) => void;
};

export const CreateOrganizationOwnerForm = ({ form, onNext }: Props) => {
  useLocale();

  const { previousStep } = useStep();
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(null);

  const { data: usersData, isFetching } = trpc.admin.listUsers.useQuery(
    { page: 1, perPage: 20, email: searchEmail, disabled: false },
    { enabled: mode === 'existing' },
  );

  const users: RouterOutputs['admin']['listUsers'] = usersData ?? {
    data: [],
    perPage: 20,
    page: 1,
    totalPages: 1,
    total: 0,
  };

  const handleContinue = () => {
    if (mode === 'existing') {
      if (!selectedUserId || !selectedUserEmail) {
        toast.error(t('auth.pleaseSelectAUser'));
        return;
      }
      onNext({ mode: 'existing', userId: selectedUserId, email: selectedUserEmail });
    } else {
      void form.handleSubmit((data) => {
        onNext({ mode: 'new', data });
      })();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex overflow-hidden rounded-lg border">
        <button
          className={`flex flex-1 items-center justify-center gap-2 py-3 font-medium text-sm transition-colors ${
            mode === 'new'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
          onClick={() => setMode('new')}
          type="button"
        >
          <UserPlusIcon className="h-4 w-4" />
          {t('auth.newUser')}{' '}
        </button>
        <button
          className={`flex flex-1 items-center justify-center gap-2 py-3 font-medium text-sm transition-colors ${
            mode === 'existing'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
          onClick={() => setMode('existing')}
          type="button"
        >
          <UserIcon className="h-4 w-4" />
          {t('auth.existingUser')}{' '}
        </button>
      </div>

      {mode === 'new' ? (
        <Form {...form}>
          <form className="space-y-4">
            <fieldset className="space-y-4" disabled={form.formState.isSubmitting}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('reports.firstName2')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('reports.firstName2')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('reports.lastName2')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('reports.lastName2')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('reports.phone')}{' '}
                      <span className="font-normal text-muted-foreground">
                        {t('auth.optional')}
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder={t('auth.phoneNumber')} type="tel" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.emailAddress')}</FormLabel>
                    <FormControl>
                      <Input placeholder="owner@example.com" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>
          </form>
        </Form>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <SearchIcon className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="ps-9"
              onChange={(e) => setSearchEmail(e.target.value)}
              placeholder={t('auth.searchByEmail')}
              value={searchEmail}
            />
          </div>
          <ScrollArea className="h-64 rounded-md border">
            {isFetching ? (
              <div className="flex h-full items-center justify-center py-8 text-muted-foreground text-sm">
                {t('auth.searching')}{' '}
              </div>
            ) : users.data.length === 0 ? (
              <div className="flex h-full items-center justify-center py-8 text-muted-foreground text-sm">
                {t('auth.noUsersFound')}{' '}
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {users.data.map((user) => (
                  <button
                    className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-start transition-colors ${
                      selectedUserId === user.id
                        ? 'border border-primary/30 bg-primary/10'
                        : 'hover:bg-muted'
                    }`}
                    key={user.id}
                    onClick={() => {
                      setSelectedUserId(user.id);
                      setSelectedUserEmail(user.email ?? '');
                    }}
                    type="button"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 font-medium text-primary text-xs">
                      {user.firstName?.[0]}
                      {user.lastName?.[0]}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-sm">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="truncate text-muted-foreground text-xs">{user.email}</p>
                    </div>
                    {selectedUserId === user.id && (
                      <CheckCircle2Icon className="ms-auto h-4 w-4 shrink-0 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
          {selectedUserId && selectedUserEmail && (
            <p className="text-muted-foreground text-sm">
              {t('copy.selected')}{' '}
              <span className="font-medium text-foreground">{selectedUserEmail}</span>
            </p>
          )}
        </div>
      )}

      <div className="flex justify-between">
        <Button onClick={() => previousStep()} type="button" variant="outline">
          {t('auth.back')}{' '}
        </Button>
        <Button onClick={handleContinue} type="button">
          {t('auth.continue')}{' '}
        </Button>
      </div>
    </div>
  );
};
